import { useEffect, useState } from "react";
import { Beaker, Loader as LoaderIcon, Trophy, Clock, ShieldAlert, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import {
  Modal, PrimaryButton, GhostButton, Pill, Loader,
  NAVY, CHAMPAGNE, GOLD, IVORY, SLATE, GREEN_LEAF, RED_LEAF,
} from "@/components/altiora/PremiumUI";


const RISK_META = {
  low: { label: "Faible", color: "#065F46", tone: "green" },
  medium: { label: "Modéré", color: "#B45309", tone: "amber" },
  high: { label: "Élevé", color: "#B91C1C", tone: "red" },
  critical: { label: "Critique", color: "#7F1D1D", tone: "red" },
};


/**
 * ActionSimulatorModal — mini-simulateur inline.
 *
 * Compare 3 timings pour une action donnée : maintenant / 1 semaine / 1 mois.
 * Réutilise l'endpoint /api/simulator/simulate avec le contexte hot_prospect_timing.
 */
export default function ActionSimulatorModal({ action, open, onClose }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !action) return;
    setLoading(true);
    api.post("/simulator/simulate", {
      context: "hot_prospect_timing",
      scenarios: [
        { key: "A", label: "Faire maintenant", params: { delay_days: 0, action_intensity: "high" } },
        { key: "B", label: "Attendre 1 semaine", params: { delay_days: 7, action_intensity: "medium" } },
        { key: "C", label: "Reporter d'un mois", params: { delay_days: 30, action_intensity: "low" } },
      ],
    })
      .then((r) => setResult(r.data))
      .catch(() => setResult(null))
      .finally(() => setLoading(false));
  }, [open, action]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Simuler · ${action?.title || ""}`}
      description="Compare l'impact selon le moment où tu réalises cette action."
      testid="action-simulator-modal"
    >
      {loading && <Loader label="Projection en cours…" />}

      {!loading && result && (
        <div className="space-y-4">
          {/* Verdict */}
          <div className="rounded-lg p-4"
               style={{ background: `${NAVY}`, color: IVORY }}
               data-testid="action-sim-verdict">
            <div className="flex items-center gap-2 mb-2" style={{ color: GOLD }}>
              <Trophy className="h-3.5 w-3.5" strokeWidth={2} />
              <span className="text-[10px] font-mono uppercase tracking-[0.28em]">
                Verdict du Directeur
              </span>
            </div>
            <p className="text-[14px] leading-relaxed font-serif">
              {result.director_verdict}
            </p>
          </div>

          {/* Compact scenario grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {result.scenarios.map((s) => {
              const isWinner = s.key === result.recommended_key;
              const risk = RISK_META[s.risk_level] || RISK_META.medium;
              return (
                <div key={s.key}
                     className="rounded-lg p-3 bg-white"
                     style={{
                       border: isWinner ? `2px solid ${GOLD}` : `1px solid ${CHAMPAGNE}33`,
                       boxShadow: isWinner ? "0 4px 12px -4px rgba(245,214,135,0.4)" : "none",
                     }}
                     data-testid={`action-sim-card-${s.key}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em]" style={{ color: CHAMPAGNE }}>
                      Scénario {s.key}
                    </div>
                    {isWinner && (
                      <Pill tone="amber">
                        <Trophy className="h-2.5 w-2.5 mr-0.5 inline" />
                        Recommandé
                      </Pill>
                    )}
                  </div>
                  <div className="font-serif text-[15px] tracking-tight" style={{ color: NAVY }}>
                    {s.label}
                  </div>
                  <div className="mt-2 space-y-1 text-[11px]">
                    <div className="flex items-center gap-1.5" style={{ color: NAVY }}>
                      <TrendingUp className="h-3 w-3" style={{ color: s.trajectory_delta >= 0 ? "#065F46" : "#B91C1C" }} />
                      Trajectoire {s.trajectory_delta > 0 ? "+" : ""}{s.trajectory_delta} pts
                    </div>
                    <div className="flex items-center gap-1.5" style={{ color: NAVY }}>
                      <span style={{ color: GOLD }}>$</span>
                      {Math.round(s.revenue_delta_cad).toLocaleString("fr-CA")} $ CAD
                    </div>
                    <div className="flex items-center gap-1.5" style={{ color: NAVY }}>
                      <ShieldAlert className="h-3 w-3" style={{ color: risk.color }} />
                      Risque {risk.label.toLowerCase()}
                    </div>
                    <div className="flex items-center gap-1.5" style={{ color: SLATE }}>
                      <Clock className="h-3 w-3" />
                      {s.time_hours} h · confiance {s.confidence}/100
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Comparative summary */}
          <p className="text-[11px] text-center italic" style={{ color: SLATE }}
             data-testid="action-sim-summary">
            {result.comparative_summary}
          </p>
        </div>
      )}
    </Modal>
  );
}
